import { Link } from "react-router-dom";
import { Twitter, Send, MessageCircle, Instagram } from "lucide-react";
import { GoldMark } from "@/components/common";
import { useLang } from "@/i18n";
import { SITE } from "@/config/site";

function SocialLink({ href, label, Icon }) {
  const { t } = useLang();
  if (!href) {
    return (
      <span className="flex items-center gap-2 text-sm text-mist/50" data-testid={`social-${label.toLowerCase()}`}>
        <Icon size={15} /> {label}
        <span className="mono-chip !py-0.5 text-[9px]">{t("common.comingSoon")}</span>
      </span>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`social-${label.toLowerCase()}`}
      className="flex items-center gap-2 text-sm text-mist transition-colors hover:text-neon">
      <Icon size={15} /> {label}
    </a>
  );
}

export function Footer() {
  const { t } = useLang();
  const cols = [
    {
      title: t("footer.explore"),
      links: [
        { to: "/", label: t("nav.home") },
        { to: "/about", label: t("nav.about") },
        { to: "/tokenomics", label: t("nav.tokenomics") },
        { to: "/roadmap", label: t("nav.roadmap") },
      ],
    },
    {
      title: t("footer.ecosystem"),
      links: [
        { to: "/nfts", label: t("nav.nfts") },
        { to: "/mint", label: t("nav.mint") },
        { to: "/ecosystem", label: t("footer.app") },
        { to: "/ecosystem", label: t("footer.card") },
      ],
    },
    {
      title: t("footer.resources"),
      links: [
        { to: "/whitepaper", label: t("nav.whitepaper") },
        { to: "/tokenomics", label: t("footer.contract") },
        { to: "/transparency", label: t("nav.transparency") },
        { to: "/faq", label: t("nav.faq") },
      ],
    },
    {
      title: t("footer.legal"),
      links: [
        { to: "/terms", label: t("footer.terms") },
        { to: "/privacy", label: t("footer.privacy") },
        { to: "/risks", label: t("footer.risks") },
      ],
    },
  ];

  return (
    <footer className="relative mt-24 border-t border-white/[0.07] bg-[#04060a]" data-testid="site-footer">
      <div className="section-pad grid gap-12 py-16 lg:grid-cols-[1.4fr_2fr]">
        <div>
          <div className="flex items-center gap-3">
            <GoldMark size={40} />
            <span className="font-display text-2xl uppercase text-paper">Maga<span className="gold-text">Brazil</span></span>
          </div>
          <p className="mt-4 max-w-xs font-heading text-xs uppercase tracking-[0.25em] text-gold/80">{t("footer.tag")}</p>
          <div className="mt-6 space-y-2.5">
            <p className="font-heading text-[11px] font-bold uppercase tracking-[0.25em] text-paper">{t("footer.social")}</p>
            <SocialLink href={SITE.socials.x} label="X / Twitter" Icon={Twitter} />
            <SocialLink href={SITE.socials.telegram} label="Telegram" Icon={Send} />
            <SocialLink href={SITE.socials.discord} label="Discord" Icon={MessageCircle} />
            <SocialLink href={SITE.socials.instagram} label="Instagram" Icon={Instagram} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
          {cols.map((c) => (
            <div key={c.title}>
              <p className="font-heading text-[11px] font-bold uppercase tracking-[0.25em] text-goldlight">{c.title}</p>
              <ul className="mt-4 space-y-2.5">
                {c.links.map((l, i) => (
                  <li key={`${l.to}-${i}`}>
                    <Link to={l.to} className="text-sm text-mist transition-colors hover:text-neon">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t border-white/[0.06]">
        <div className="section-pad space-y-4 py-8">
          <p className="max-w-4xl text-xs leading-relaxed text-mist/80" data-testid="footer-disclaimer">
            {t("footer.disclaimer")}
          </p>
          <p className="font-mono text-[11px] uppercase tracking-widest text-mist/60">
            © {new Date().getFullYear()} MAGABRAZIL. {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}
